/**
 * ForjaRadarCard - Intelligence feed with clickable opportunity items
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logRequest } from '@/hooks/useRequestLogger';
import { Radio, TrendingUp, ArrowRight, Crown, Play, Lock, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useRole } from '@/contexts/RoleContext';
import { usePVRedirect } from '@/hooks/usePVRedirect';

interface RadarFeedItem {
  id: string;
  icon: React.ElementType;
  title: string;
  subtitle: string;
  timestamp: string;
  link: string;
  color: string;
}

function pickFromList<T>(list: T[], hourIndex: number, offset: number): T | undefined {
  if (!list.length) return undefined;
  return list[(hourIndex + offset) % list.length];
}

async function fetchRadarFeed(hourIndex: number): Promise<RadarFeedItem[]> {
  const items: RadarFeedItem[] = [];

  // Fetch multiple BR products
  const { data: brProducts } = await supabase
    .from('trending_products_cache')
    .select('id, name, region')
    .eq('region', 'BR')
    .order('rank', { ascending: true })
    .limit(10);

  const pickedBR = pickFromList(brProducts ?? [], hourIndex, 0);
  if (pickedBR) {
    items.push({
      id: `product-br-${pickedBR.id}`,
      icon: TrendingUp,
      title: pickedBR.name,
      subtitle: 'Produto viral detectado · 🇧🇷',
      timestamp: 'Detectado hoje',
      link: '/arsenal?tab=produtos&region=BR',
      color: 'text-amber-500',
    });
  }

  // Fetch multiple US products
  const { data: usProducts } = await supabase
    .from('trending_products_cache')
    .select('id, name, region')
    .eq('region', 'US')
    .order('rank', { ascending: true })
    .limit(10);

  const pickedUS = pickFromList(usProducts ?? [], hourIndex, 3);
  if (pickedUS) {
    items.push({
      id: `product-us-${pickedUS.id}`,
      icon: TrendingUp,
      title: pickedUS.name,
      subtitle: 'Produto viral detectado · 🇺🇸',
      timestamp: 'Detectado hoje',
      link: '/arsenal?tab=produtos&region=US',
      color: 'text-amber-500',
    });
  }

  // Fetch multiple creators by GMV
  const { data: creators } = await supabase
    .from('arsenal_creators')
    .select('id, nickname, username, gmv_cents')
    .gt('gmv_cents', 0)
    .order('gmv_cents', { ascending: false })
    .limit(10);

  const pickedCreator = pickFromList(creators ?? [], hourIndex, 7);
  if (pickedCreator) {
    const gmv = (pickedCreator.gmv_cents ?? 0) / 100;
    const gmvLabel = gmv >= 1_000_000 ? `$${(gmv / 1_000_000).toFixed(1)}M` : `$${(gmv / 1_000).toFixed(0)}K`;
    items.push({
      id: `creator-${pickedCreator.id}`,
      icon: Crown,
      title: pickedCreator.nickname || pickedCreator.username,
      subtitle: `Criador com GMV de ${gmvLabel}`,
      timestamp: 'Em destaque',
      link: '/arsenal?tab=criadores',
      color: 'text-purple-500',
    });
  }

  // Fetch multiple recent videos
  const { data: videos } = await supabase
    .from('arsenal_videos')
    .select('id, video_caption, published_at')
    .eq('intention', 'tiktok_shop')
    .eq('publication_type', 'normal')
    .order('published_at', { ascending: false })
    .limit(10);

  const pickedVideo = pickFromList(videos ?? [], hourIndex, 5);
  if (pickedVideo) {
    items.push({
      id: `video-${pickedVideo.id}`,
      icon: Play,
      title: 'Vídeo viral descoberto',
      subtitle: pickedVideo.video_caption?.slice(0, 50) || 'Vídeo com alto potencial de vendas',
      timestamp: 'Recém adicionado',
      link: '/arsenal?tab=videos',
      color: 'text-rose-500',
    });
  }

  logRequest('marketplace_products');
  logRequest('arsenal_creators');
  logRequest('arsenal_videos');
  return items;
}

function ForjaRadarCardInner() {
  const { effectiveRole } = useRole();
  const { redirectToPV } = usePVRedirect();
  const isVisitor = effectiveRole === 'visitor';
  const hourIndex = Math.floor(Date.now() / 3_600_000);

  const { data: feedItems = [], isLoading } = useQuery<RadarFeedItem[]>({
    queryKey: ['forja-radar-feed', hourIndex],
    queryFn: () => fetchRadarFeed(hourIndex),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="forja-card rounded-xl p-3 md:p-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 md:w-7 md:h-7 rounded-lg bg-primary/10 flex items-center justify-center">
            <Radio className="w-3 h-3 md:w-3.5 md:h-3.5 text-primary" />
          </div>
          <h2 className="font-display text-sm md:text-base font-bold text-foreground">
            Radar da Forja
          </h2>
        </div>
        {!isVisitor && (
          <Link to="/arsenal" className="text-[10px] text-primary/70 hover:text-primary transition-colors flex items-center gap-1">
            Ver tudo <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-1.5 flex-1">
          {[1, 2].map(i => <Skeleton key={i} className="h-12 rounded-lg" />)}
        </div>
      ) : (
        <div className="space-y-1.5 flex-1">
          {feedItems.map((item) => {
            const Icon = item.icon;
            const content = (
                <div
                key={item.id}
                className={cn(
                  "flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg",
                  "bg-secondary/40 border border-border/30",
                  "hover:bg-secondary/60 transition-colors cursor-pointer"
                )}
              >
                <div className={cn("w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0", "bg-secondary/80")}>
                  <Icon className={cn("w-4 h-4", item.color)} />
                </div>
                <div className="flex-1 min-w-0">
                  {isVisitor ? (
                    <>
                      <p className="text-xs font-medium text-foreground truncate blur-[3px] select-none">
                        {item.title}
                      </p>
                      <p className="text-[10px] text-muted-foreground">{item.subtitle}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-medium text-foreground truncate">{item.title}</p>
                      <p className="text-[10px] text-muted-foreground">{item.subtitle}</p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-[9px] text-muted-foreground/70 hidden sm:block">{item.timestamp}</span>
                  {isVisitor ? (
                    <Lock className="w-3 h-3 text-muted-foreground/40" />
                  ) : (
                    <ArrowRight className="w-3 h-3 text-muted-foreground/40" />
                  )}
                </div>
              </div>
            );

            if (isVisitor) {
              return <div key={item.id} onClick={() => redirectToPV('radar-feed')}>{content}</div>;
            }
            return <Link key={item.id} to={item.link}>{content}</Link>;
          })}
        </div>
      )}

      {isVisitor && (
        <Button
          size="sm"
          variant="outline"
          className="w-full gap-1.5 text-xs mt-auto"
          onClick={() => redirectToPV('radar-card')}
        >
          Quero ser membro
          <ExternalLink className="w-3.5 h-3.5" />
        </Button>
      )}
    </div>
  );
}

const ForjaRadarCard = React.memo(ForjaRadarCardInner);
export default ForjaRadarCard;
